// Task #580 Phase 3b shared helper.
//
// Builds the config fields that activate Last-Event-ID resume
// against the BE broker (Task #579 / mmagent-chat-ui 0.1.75+). All four
// chat surfaces (IDA, MyVault, Meridian Structure Agent, BKG) wire the
// same shape; centralizing here avoids drift between consumers.
//
// Defaults match Phase 3a (`useSendMessageWithFiles`): 3 reconnect
// attempts, 2000ms exponential backoff. Override on the per-panel
// chatConfig spread if a surface needs different ceilings.
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import type { ChatMessageType } from "@metalmind-ab/mmagent-chat-ui";

import { getSyncAuthToken } from "./getSyncAuthToken";
import { appendStreamInterruptedMarkerOnce } from "./streamInterruptedMarker";
import {
  hasAcceptedChatTerminal,
  rememberAcceptedChatTerminal,
} from "./acceptedChatTerminals";

type ResumeFields = {
  enableAutoReconnect: boolean;
  subscribeUrlBuilder: (conversationId: string | number) => string;
  subscribeHeadersBuilder: () => Record<string, string>;
  onStreamDropped: (attempt: number, maxAttempts: number) => void;
  onReplayUnavailable: (submissionId?: string) => void;
  onTerminalAccepted: (terminal: {
    submissionId: string;
    kind: "done" | "error";
  }) => void;
  maxReconnectAttempts: number;
  reconnectBackoffMs: number;
};

/**
 * Returns ChatConfig fields that enable Last-Event-ID resume against the
 * BE broker. `apiBaseUrl` is the same value the consumer panel already
 * computed for `ChatConfig.apiBaseUrl`.
 */
export function buildChatResumeOptions(apiBaseUrl: string): ResumeFields {
  return {
    enableAutoReconnect: true,
    maxReconnectAttempts: 3,
    reconnectBackoffMs: 2000,
    subscribeUrlBuilder: (conversationId) =>
      `${apiBaseUrl}/chat/conversations/${conversationId}/messages/stream`,
    subscribeHeadersBuilder: () => {
      // Read token fresh per retry call. Library fires this synchronously on
      // each EventSource open including reconnect retries (Task #604 race
      // fix in 0.1.77) -- closure capture would freeze a stale token. Empty
      // headers if no token cached so we send no Authorization rather than
      // "Bearer null".
      const token = getSyncAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return headers;
    },
    onStreamDropped: (attempt, maxAttempts) => {
      // Library will retry automatically; surface to console for ops/debug.
      console.warn(
        `[chat] SSE stream dropped, reconnect attempt ${attempt}/${maxAttempts}`,
      );
    },
    onTerminalAccepted: ({ submissionId, kind }) => {
      rememberAcceptedChatTerminal(submissionId, kind);
    },
    onReplayUnavailable: (submissionId) => {
      // BE lost the cursor and is replaying the buffer wholesale --
      // user may have seen content that will now be re-rendered.
      // Mark the last assistant message to flag the interruption.
      const state = useChatStore.getState();
      const lastAssistant = [...(state.messages as ChatMessageType[])]
        .reverse()
        .find((m) => m.role === "assistant");
      if (!lastAssistant || hasAcceptedChatTerminal(submissionId)) return;

      // History maps saved partial responses with isStreaming=false too. Only
      // a terminal frame accepted for this exact submission proves that the
      // response completed; a different or missing identity remains visible.
      appendStreamInterruptedMarkerOnce(lastAssistant.id);
    },
  };
}
